import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container, IconButton, Menu, MenuItem, Typography } from '@material-ui/core';
import AnimatedNumber from 'animated-number-react';
import clsx from 'clsx';

import { useStyles } from 'features/app/components/BaseBalanceDropdown/styles';
import type { BalanceCurrency } from 'features/app/ts/BalanceCurrency';
import { formatCurrencyNumber } from 'features/app/utils/formatCurrencyNumber';
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow-down-icon.svg';
import i18n from 'locales/i18n';

const scrollListener = (e: Event) => {
  e.preventDefault();
  window.scrollTo(0, 0);
};

type BalanceDropdownProps = {
  displayedBalance: BalanceCurrency | undefined;
  onChange?: (item: BalanceCurrency) => void;
  options?: BalanceCurrency[];
  bordered?: boolean;
  customContainerClassName?: string;
  title?: string;
};

const BaseBalanceDropdown: React.FC<BalanceDropdownProps> = ({
  displayedBalance,
  onChange,
  options,
  customContainerClassName,
  bordered = true,
  title,
}) => {
  const direction = i18n.dir()

  const classes = useStyles({ bordered, direction: direction });

  const ref = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [menuShown, setMenuShown] = useState(false);

  useEffect(() => {
    ref && setAnchorEl(ref.current);
  }, [ref]);

  const toggleMenu = useCallback(() => {
    const newValue = !menuShown;

    if (newValue) {
      window.addEventListener('scroll', scrollListener);
    } else {
      window.removeEventListener('scroll', scrollListener);
    }

    setMenuShown(newValue);
  }, [menuShown]);

  return (
    <Container
      className={clsx(
        classes.root,
        customContainerClassName && { [customContainerClassName]: !!customContainerClassName }
      )}
    >
      {title ? (
        <Container className={classes.titleContainer}>
          <Typography className={classes.title}>{title}</Typography>
        </Container>
      ) : null}
      <Container className={classes.balanceContainer} ref={ref}>
        <Container className={classes.balanceItems}>
          <Typography className={classes.balance} variant="h3">
            <AnimatedNumber value={displayedBalance?.value} formatValue={formatCurrencyNumber} />{' '}
            {displayedBalance?.currency_code?.toUpperCase()}
          </Typography>

          {options && options?.length > 0 && (
            <>
              <IconButton className={classes.arrowButton} onClick={toggleMenu}>
                <ArrowIcon />
              </IconButton>
              <Menu
                open={menuShown}
                onClose={toggleMenu}
                keepMounted
                anchorEl={anchorEl}
                disableScrollLock={true}
                classes={{
                  paper: classes.menu,
                }}
              >
                {options?.map((value, index) => (
                  <MenuItem
                    className={classes.menuItem}
                    onClick={() => {
                      onChange && onChange(value);
                      toggleMenu();
                    }}
                    key={value.id}
                  >
                    <Container className={clsx({ [classes.menuItemCurrency]: index < options?.length - 1 })}>
                      {Number(value.value).toLocaleString('currency', { maximumFractionDigits: 2 })}{' '}
                      {value.currency_code.toUpperCase()}
                    </Container>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Container>
      </Container>
    </Container>
  );
};

export default BaseBalanceDropdown;
